import { useState } from 'react'

import { useAction } from '../components/toast'
import { Badge, Card, Loaded, Modal, Notice, Page, ReasonDialog, Table } from '../components/ui'
import { del, get, put } from '../lib/api'
import { dateTime } from '../lib/format'
import { useResource } from '../lib/useResource'

type SignInCredential = {
  provider: string
  configured: boolean
  /** 'console' | 'environment' | 'unset' — which layer is in force. */
  source: string
  /** The identifier, returned in full: it is public by design. */
  public_value: string
  /** What to call it on screen — "App ID", "Client IDs", "Browser key". */
  public_label: string
  /** True when there is no secret half at all. */
  public_only: boolean
  environment_fallback: boolean
  updated_by: string | null
  updated_at: string | null
}

const TITLES: Record<string, string> = {
  google_oauth: 'Google sign-in',
  apple_oauth: 'Apple sign-in',
  facebook: 'Facebook sign-in',
  maps: 'Google Maps (this console)',
}

const NOTES: Record<string, string> = {
  google_oauth:
    'The client IDs an id_token is checked against. One per build — Android, iOS, web — comma separated. A token is valid for whichever client minted it, so every ID in use has to be listed here or that build cannot sign in.',
  apple_oauth:
    'The service IDs an Apple id_token is checked against. Comma separated, same reasoning as Google.',
  facebook:
    'The App ID and App Secret. Both are needed: a token is confirmed against Facebook with an app token of the form id|secret, and skipping that check is the classic Facebook login vulnerability — any app’s token would work.',
  maps:
    'The browser key this console loads Google Maps with, for the dispatch map. Restrict it by HTTP referrer in the Google Cloud console. It is not used by the phone app.',
}

export function SignIn() {
  const credentials = useResource((signal) =>
    get<SignInCredential[]>('/v1/admin/sign-in/credentials', undefined, signal),
  )
  const run = useAction()
  const [editing, setEditing] = useState<SignInCredential | null>(null)
  const [clearing, setClearing] = useState<SignInCredential | null>(null)

  return (
    <Page
      title="Sign-in & keys"
      subtitle="Which identities this deployment accepts, and the key the console draws maps with."
    >
      <Notice>
        <strong>This is the half the server enforces.</strong> Setting a Google client ID here
        changes which tokens verify; setting the Facebook secret changes which app’s tokens are
        accepted. Rotating either is a form submission rather than a deploy — which is the point,
        because a secret that needs a deploy to rotate tends not to get rotated.
      </Notice>

      <Notice tone="warn">
        <strong>The phone app’s own SDK keys are not set here, and cannot be.</strong> Google
        Sign-In reads its client ID from <span className="mono">google-services.json</span>, the
        Android Maps SDK reads <span className="mono">com.google.android.geo.API_KEY</span> from
        the merged manifest, and the Facebook SDK reads{' '}
        <span className="mono">facebook_app_id</span> from <span className="mono">strings.xml</span>{' '}
        — all before any network call this server could answer. Those stay build-time values by
        the platforms’ design. When you change an ID here, change it in the app build too, or the
        app will present a token this server no longer accepts.
      </Notice>

      <Card
        title="Credentials"
        hint="Identifiers are shown; secrets never are"
        flush
      >
        <Loaded resource={credentials}>
          {(rows) => (
            <Table
              rows={rows}
              rowKey={(row) => row.provider}
              empty="Nothing to configure."
              columns={[
                {
                  key: 'name',
                  header: 'Provider',
                  strong: true,
                  render: (row) => TITLES[row.provider] ?? row.provider,
                },
                {
                  key: 'value',
                  header: 'Identifier',
                  render: (row) =>
                    row.public_value ? (
                      <span className="mono" style={{ fontSize: 11.5 }}>
                        {row.public_value}
                      </span>
                    ) : (
                      <span className="dim">— not set —</span>
                    ),
                },
                {
                  key: 'secret',
                  header: 'Secret',
                  render: (row) =>
                    row.public_only ? (
                      <span className="dim">n/a</span>
                    ) : row.configured ? (
                      <Badge tone="green">Set</Badge>
                    ) : (
                      <Badge tone="orange">Missing</Badge>
                    ),
                },
                {
                  key: 'source',
                  header: 'From',
                  render: (row) =>
                    row.source === 'console' ? (
                      <Badge tone="cyan">Console</Badge>
                    ) : row.source === 'environment' ? (
                      <Badge>Environment</Badge>
                    ) : (
                      <span className="dim">—</span>
                    ),
                },
                {
                  key: 'when',
                  header: 'Updated',
                  render: (row) =>
                    row.updated_at ? dateTime(row.updated_at) : <span className="dim">—</span>,
                },
                {
                  key: 'act',
                  header: '',
                  render: (row) => (
                    <div className="row">
                      <button className="btn btn--sm" onClick={() => setEditing(row)}>
                        {row.source === 'console' ? 'Replace…' : 'Set…'}
                      </button>
                      {row.source === 'console' && (
                        <button className="btn btn--sm btn--ghost" onClick={() => setClearing(row)}>
                          Clear
                        </button>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          )}
        </Loaded>
      </Card>

      {editing && (
        <EditDialog
          credential={editing}
          onClose={() => setEditing(null)}
          onSave={(publicValue, apiKey, reason) =>
            run(
              () =>
                put(`/v1/admin/sign-in/credentials/${editing.provider}`, {
                  public_value: publicValue,
                  api_key: apiKey,
                  reason,
                }),
              `${TITLES[editing.provider] ?? editing.provider} updated`,
              credentials.reload,
            )
          }
        />
      )}

      {clearing && (
        <ReasonDialog
          title={`Clear ${TITLES[clearing.provider] ?? clearing.provider}`}
          description={
            clearing.environment_fallback
              ? 'The console setting is removed and this deployment’s environment value takes over again.'
              : 'The console setting is removed and there is no environment value behind it — this provider will stop working until something is set.'
          }
          confirmLabel="Clear"
          danger
          onConfirm={(reason) =>
            run(
              () =>
                del(`/v1/admin/sign-in/credentials/${clearing.provider}`, { reason }),
              'Cleared',
              credentials.reload,
            )
          }
          onClose={() => setClearing(null)}
        />
      )}
    </Page>
  )
}

/**
 * Both halves in one form.
 *
 * A Facebook app ID without its secret verifies nothing, and a secret paired
 * with the previous app's ID is how a deployment ends up debugging one app's
 * token against another app's credentials — so they are submitted together
 * rather than in two saves with a window between them.
 */
function EditDialog({
  credential,
  onSave,
  onClose,
}: {
  credential: SignInCredential
  onSave: (publicValue: string, apiKey: string, reason: string) => Promise<unknown>
  onClose: () => void
}) {
  const [publicValue, setPublicValue] = useState(credential.public_value)
  const [apiKey, setApiKey] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const needsSecret = !credential.public_only
  const ready = publicValue.trim().length > 0 && reason.trim().length >= 10

  const submit = async () => {
    setBusy(true)
    try {
      await onSave(publicValue.trim(), apiKey.trim(), reason.trim())
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={TITLES[credential.provider] ?? credential.provider} onClose={onClose}>
      <div className="stack">
        <p className="muted" style={{ margin: 0 }}>
          {NOTES[credential.provider]}
        </p>

        <div className="field">
          <label className="field__label" htmlFor="public">
            {credential.public_label}
          </label>
          <input
            id="public"
            className="input mono"
            autoFocus
            value={publicValue}
            onChange={(event) => setPublicValue(event.target.value)}
            placeholder={
              credential.provider === 'facebook' ? '1122334455' : 'xxxx.apps.googleusercontent.com'
            }
          />
        </div>

        {needsSecret && (
          <div className="field">
            <label className="field__label" htmlFor="secret">
              App Secret
            </label>
            <input
              id="secret"
              className="input mono"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder={credential.configured ? 'Leave blank to keep the current secret' : ''}
            />
            <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
              Never displayed after saving — not even a prefix. Losing it means issuing another
              from the Facebook app dashboard.
            </div>
          </div>
        )}

        <div className="field">
          <label className="field__label" htmlFor="why">
            Reason (recorded in the audit log, minimum 10 characters)
          </label>
          <textarea
            id="why"
            className="textarea"
            style={{ minHeight: 72 }}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Ticket reference and what prompted this rotation"
          />
        </div>

        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={busy || !ready}
            onClick={() => void submit()}
          >
            {busy ? 'Working…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
